import {
  PERSONAL,
  MS_IN_HOUR,
  EXEC_DATETIME_TIME_RANGE_ID,
  TBAR_SPACER,
  TODO,
  IN_PROGRESS,
  DONE,
} from "../../../../global/constants";
import {
  addDays,
  areDatesSame,
  dailyResourceWorkHoursBasedOnShifts,
  formatDate,
  formatDateTime,
  removeTimezoneOffset,
  setHours,
} from "../../../../utils/DateUtils/DateUtils";
import {
  defaultEventMenuItems,
  defaultSchedulerFeatures,
  getSettings,
  schedulerExecDateTimeRanges,
} from "../configs";
import {
  DateHelper,
  DragHelper,
  DomHelper,
  Tooltip,
  Popup,
  MessageDialog,
  LocaleHelper,
  LocaleManager,
} from "@bryntum/core-thin";
import { v4 as uuidv4 } from "uuid";
import {
  getActivateTaskOnAssignValue,
  getSchedulerMode,
  reloadTaskManagementScheduler,
  saveSettings,
  setActivateTaskOnAssignValue,
  toggleSchedulerMode,
} from "../utils";
import { getUserExtendedRegulars } from "../../../../services/schedule-activity-services/scheduleActivityActions";
import { getResourceTasks } from "../../../../services/scheduler-services/schedulerActions";
import { t } from "i18next";

export const taskManagementSchedulerFeatures = (
  createTask,
  backlogsRef,
  deleteTask
) => {
  return {
    ...defaultSchedulerFeatures,
    mode: getSchedulerMode("task-management-scheduler"),
    fillTicks: false,
    maxZoomLevel: 17,
    onPresetChange: () => {},
    id: "task-management-scheduler",
    rowHeight: getSettings("work-package-scheduler-settings").rowHeight || 60,
    barMargin: getSettings("work-package-scheduler-settings").barMargin || 10,
    scheduleMenuFeature: {
      items: {
        addEvent: {
          disabled: false,
          text: t("global.createTask"),
          onItem: (e) => {
            if (!e.id) return;

            const data = {
              name: t("global.newTask"),
              startDate: removeTimezoneOffset(setHours(e.date, 9)),
              finishDate: removeTimezoneOffset(
                addDays(setHours(e.date, 16), 7)
              ),
              resourceId: e.id,
            };
            createTask(data);
          },
        },
      },
    },
    eventMenuFeature: {
      items: {
        ...defaultEventMenuItems,
        deleteEvent: false,
        customDelete: {
          text: t("global.delete"), // "Delete"
          icon: "b-fa b-fa-trash",
          onItem({ eventRecord }) {
            MessageDialog.confirm({
              title: t("global.confirmDeletion"), // "Confirm deletion"
              message: `${t("global.deletionConfirm")} '${eventRecord.name}'?`,
              okButton: t("global.yes"), // "Yes"
              cancelButton: t("global.no"), // "No"
            }).then((response) => {
              if (response === MessageDialog.okButton && deleteTask)
                deleteTask(eventRecord.id);
            });
          },
        },
      },
      processItems({ eventRecord, items }) {
        if (eventRecord.data.projectId) {
          items.goToProject = {
            text: t("global.goToProject"), // "Go to project"
            icon: "b-fa b-fa-external-link",
            onItem({ eventRecord }) {
              const backlog = backlogsRef.current.find(
                (b) =>
                  b.scheduleActivity.id === eventRecord.data.scheduleActivity.id
              );
              const projectId = backlog.projectId;
              const scenarioId = backlog.scenarioId;
              const structureId = backlog.structureId;
              const name = eventRecord.data.scheduleActivity.name;
              window.open(
                `${window.location.origin}/process/view/gantt/${projectId}/${scenarioId}/${structureId}?selectedActivity=${name}`,
                "_blank",
                "noopener noreferrer"
              );
            },
          };
        }
      },
      eventEditFeature: { disabled: true },
    },
    eventBodyTemplate: (data) =>
      data.duration === 0
        ? `
          <section class="event-content">
          </section>
          <div class="right-block">
            <label style="font-size: 12px; font-weight: bold; margin-top: -15px; opacity: 0.55; color: #000; display: block;">${data.name}</label>
            <label style="font-size: 9px; opacity: 0.8; margin-top: 8px; color: #666; display: block;">${data.projectName}</label>
          </div>
        `
        : `
          <section class="event-content">
            <div class="left-block">
              <div class="name-label ">${data.name}</div>
              <div class="project-label">${data.projectName}</div>
            </div>
          </section>
        `,
    eventTooltipFeature: {
      template: (data) => {
        let customColumnsHtml = "";
        if (data.eventRecord.data?.customColumns) {
          for (const [key, value] of Object.entries(
            data.eventRecord.data.customColumns
          )) {
            if (key !== "id") {
              customColumnsHtml += `<dd><b>${key}:</b> ${value}</dd>`;
            }
          }
        }

        return `
          <dl style="margin-bottom: 0;">
            <dd><b>${data.eventRecord.name}</b></dd>
            <dd><b>${t("global.parent")}:</b> ${
              data.eventRecord.scheduleActivity?.name || ""
            }</dd>
            <dd><b>${t("global.progress")}</b>: ${data.eventRecord.progress}%</dd>
            <dd>${data.startClockHtml}</dd>
            <dd>${data.endClockHtml}</dd>
            ${customColumnsHtml}
          </dl>
        `;
      },
    },
    calendarHighlightFeature: {
      calendar: "resource",
      collectAvailableResources({ scheduler, eventRecords }) {
        const activity = eventRecords[0].scheduleActivity;

        if (!activity) return;

        const activityResourceIds = Object.keys(
          activity.ganttResourceAllocation
        ).map((res) => +res);
        return scheduler.resourceStore.query((resource) => {
          const parentIds = resource.parentIds.split(",").map((id) => +id);
          return !!activityResourceIds.filter((id) => parentIds.includes(id))
            .length;
        });
      },
    },
    eventDragFeature: {
      constrainDragToTimeline: false,
      validatorFn({ eventRecords, newResource, externalDropTarget }) {
        const isPersonalTasks = eventRecords.some(
          (record) => record.projectName === PERSONAL
        );

        // if dragged to the unplanned tasks grid
        if (externalDropTarget?.id === "unplannedTasksGrid") {
          if (isPersonalTasks) {
            return { valid: false, message: t("myTasks.cantRemovePersonalTask") };
          }
          return { valid: true, message: t("myTasks.removeTask") };
        }

        if (isPersonalTasks) {
          return { valid: true }; // TODO need to discuss later
        }

        // check if resource`s team is assigned for this activity
        const activityResourceIds = eventRecords.map((record) => {
          const allocation =
            record.scheduleActivity?.ganttResourceAllocation || {};
          return Object.keys(allocation).map((res) => +res);
        });
        const teamIds = newResource.parentIds.split(",").map((id) => +id);
        const isValid = !!activityResourceIds.every((arr) => {
          return !!arr.filter((value) => teamIds.includes(value)).length;
        });

        return {
          valid: isValid,
          message: isValid ? "" : t("myTasks.cantAssignOnResource"),
        };
      },
      externalDropTargetSelector: "#unplannedTasksGrid",
    },
    percentBarFeature: {
      disabled: false,
      showPercentage: false,
      valueField: "progress",
    },
  };
};

export const taskManagementAdditionalButtons = (
  schedulerId,
  schedulerRef,
  resourceUtilization,
  isReady,
  selectedDatesRef,
  dispatch
) => {
  return [
    {
      type: "buttonGroup",
      items: ["ALL", TODO, IN_PROGRESS, DONE].map((name) => {
        return {
          ref: name.toLowerCase(),
          text: name ? t(`taskFocus.status.${name}`) : "", 
          pressed: name === "ALL",
          enableToggle: true,
          toggleGroup: "status",
          onAction() {
            const taskStore = schedulerRef.current.instance.project.taskStore;
            taskStore.filter({
              id: "statusFilter",
              filterBy: (task) => name === "ALL" || task.status === name,
            });
          },
        };
      }),
    },
    TBAR_SPACER,
    {
      type: "button",
      icon: "b-fa b-fa-refresh",
      tooltip: t("global.refreshData"),
      cls: "b-tbar-element",
      onClick: async (event) => {
        event.source.disabled = true;
        event.source.byRef.iconElement.className += " fa-spin";

        await reloadTaskManagementScheduler(
          schedulerRef,
          isReady,
          selectedDatesRef,
          dispatch
        );

        setTimeout(() => {
          event.source.disabled = false;
          event.source.byRef.iconElement.className = "b-fa b-fa-refresh";
        }, 1000); // setTimeout to avoid spamming
      },
    },
    {
      type: "button",
      icon: "b-icon b-fa-calendar-check",
      toggleable: true,
      tooltip: t("resourceFocus.showResourceTable"),
      onAction: () => {
        if (resourceUtilization.current?.instance.hidden) {
          resourceUtilization.current?.instance.show();
        } else {
          resourceUtilization.current?.instance.hide();
        }
      },
    },
    // {
    //   type: "button",
    //   icon: `b-fa-solid b-fa-grip-${getSchedulerMode(schedulerId, true)}`,
    //   tooltip: `Toggle to ${getSchedulerMode(schedulerId, true)} mode`,
    //   pressed: getSchedulerMode(schedulerId) === "vertical",
    //   toggleable: true,
    //   onToggle: ({ pressed }) => {
    //     toggleSchedulerMode(schedulerId, pressed, dispatch);
    //   },
    // },
    {
      ref: "settingsButton",
      type: "button",
      icon: "b-fa-sliders-h",
      tooltip: t("global.settings.viewSettings"),
      cls: "b-tbar-element",
      menu: {
        cls: "settings-menu",
        layoutStyle: {
          flexDirection: "column",
        },
        defaults: {
          type: "slider",
          showValue: true,
        },
        items: [
          {
            text: t("global.settings.rowHeight"),
            value: getSettings(`${schedulerId}-settings`).rowHeight || 50,
            min: 30,
            max: 70,
            onAction({ value }) {
              saveSettings(schedulerId, "rowHeight", value);
            },
            onInput: ({ value }) => {
              schedulerRef.current.schedulerProInstance.rowHeight = value;
            },
          },
          {
            text: t("global.settings.barMargin"),
            value: getSettings(`${schedulerId}-settings`).barMargin || 10,
            min: 0,
            max: 10,
            onAction({ value }) {
              saveSettings(schedulerId, "barMargin", value);
            },
            onInput: ({ value }) => {
              schedulerRef.current.schedulerProInstance.barMargin = value;
            },
          },
          {
            text: t("global.settings.taskBorderRadius"),
            value: getSettings(`${schedulerId}-settings`).taskBorderRadius || 0,
            min: 0,
            max: 15,
            onAction({ value }) {
              saveSettings(schedulerId, "taskBorderRadius", value);
            },
            onInput: ({ value }) => {
              schedulerRef.current.element.style.setProperty(
                "--task-border-radius",
                `${value}px`
              );
            },
          },
        ],
      },
    },
  ];
};

export const TaskManagementSchedulerFilters = {
  selectedProjectsFilter: {
    id: "selectedProjectsFilter",
    store: "eventStore",
  },
  selectedCollabsFilter: {
    id: "selectedCollabsFilter",
    store: "resourceStore",
  },
  selectedRegularsForResourcesFilter: {
    id: "selectedRegularsForResourcesFilter",
    store: "resourceStore",
  },
  selectedRegularsFilter: {
    id: "selectedRegularsFilter",
    store: "eventStore",
  },
  teamCollabsFilter: {
    id: "teamCollabsFilter",
    store: "resourceStore",
  },
  workPackageGridEventsFilter: {
    id: "workPackageGridEventsFilter",
    store: "eventStore",
  },
  workPackageGridResourcesFilter: {
    id: "workPackageGridResourcesFilter",
    store: "eventStore",
  },
};

export const getTopBarFilterForEffortDrivenBacklog = (
  grid,
  dispatch,
  datesFilterRef,
  selectedProjects,
  selectedTeams
) => {
  return [
    {
      type: "button",
      icon: "b-fa b-fa-refresh",
      tooltip: t("global.refreshData"),
      cls: "b-tbar-element",
      onClick: (event) => {
        event.source.byRef.iconElement.className += " fa-spin";
        event.source.disabled = true;

        grid.current.instance.mask(t("global.loading..."));
        grid.current?.gridInstance.store.clearFilters();
        grid.current.gridInstance.widgetMap.searchField.value = "";
        const datesFilter = datesFilterRef.current;
        const filterForSAs = {
          ...datesFilter,
          projectNames: selectedProjects,
          resourceIds: selectedTeams,
        };
        const promises = Promise.all([
          dispatch(getUserExtendedRegulars(filterForSAs)),
          dispatch(getResourceTasks(datesFilter)),
        ]);

        promises.then(() => {
          setTimeout(() => {
            event.source.disabled = false;
            event.source.byRef.iconElement.className = "b-fa b-fa-refresh";
            grid.current.instance.unmask();
          }, 1000);
        });
      },
    },
    {
      id: "searchField",
      type: "textfield",
      cls: "filter-by-name",
      flex: "1 1 80%",
      align: "end",
      placeholder: t("taskFocus.findBacklogs"),
      value: "",
      clearable: true,
      keyStrokeChangeDelay: 100,
      triggers: {
        filter: {
          align: "end",
          cls: "b-fa b-fa-filter",
        },
      },
      onChange: ({ value }) => {
        if (value === "") {
          grid.current?.gridInstance.store.clearFilters();
        } else {
          value = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(value, "i");
          const customFields = grid.current.customColumns;
          grid.current?.gridInstance.store.filter({
            filters: (task) => {
              const matchName = task.name && task.name.match(regex);
              const projectName =
                task.projectName && task.projectName.match(regex);
              const startDate = task.startDate && task.startDate.match(regex);
              const customFieldsMatch = customFields.some((field) =>
                (task.customColumns[field] || "").match(regex)
              );

              return matchName || projectName || startDate || customFieldsMatch;
            },
            replace: true,
          });
        }
      },
    },
    {
      id: "activateOnAssignCheckbox",
      type: "checkbox",
      color: "b-blue",
      text: t("resourceFocus.activateTaskOnAssign"),
      checked: getActivateTaskOnAssignValue(),
      onAction: ({ checked }) => {
        setActivateTaskOnAssignValue(checked);
      },
    },
  ];
};

export const unassignedTasksGridConfigs = (t) => ({
  id: "unplannedTasksGrid",
  ui: "toolbar",
  flex: "0 1 25%",
  title: t("resourceFocus.unassignedBacklogs"),
  collapsible: true,
  rowHeight: 50,
  features: {
    stripe: true,
    sort: "startDate",
    group: "startDate",
    cellMenu: false,
    cellTooltip: {
      tooltipRenderer: ({ record }) => {
        return (
          <div>
            <div className="name-label">
              <b>{record.name}</b>
            </div>

            {record.saParentName && (
              <div className="sa-parent-label">{record.saParentName}</div>
            )}

            <div className="start-block">
              <i className="mr-2 b-fa b-fa-calendar"></i>
              <span>{t("global.start")}: {record.startDate}</span>
            </div>

            <div className="finish-block">
              <i className="mr-2 b-fa b-fa-calendar"></i>
              <span>{t("global.finish")}: {record.finishDate}</span>
            </div>

            {record.deadlineDate && (
              <div className="deadline-label">
                {t("global.deadline")}: {record.deadlineDate}
              </div>
            )}

            {record.execDateTime && (
              <div className="exec-datetime-label">
                {t("global.executionDatetime")}: {formatDateTime(record.execDateTime)}
              </div>
            )}
          </div>
        );
      },
    },
  },
  columns: [
    {
      text: t("global.tasks"),
      field: "name",
      flex: 1,
      minWidth: 100,
      editor: null,
      htmlEncode: false,
      renderer: ({ record }) => {
        return `
          <div>
            <div><b>${record.name}</b></div>
            <div style="font-size:0.75em"><i>${record.projectName}</i></div>
          <div>
        `;
      },
    },
    {
      text: t("global.work"),
      field: "workHoursRemaining",
      width: 110,
      align: "right",
      editor: null,
      htmlEncode: false,
      renderer: ({ record }) => {
        const hours = record.workHoursRemaining;
        if (!hours) return;

        if (hours < 1) {
          const mins = (hours * 60).toFixed(0);
          const unit = mins === 1 ? "min" : "mins";
          return `${mins} ${unit}`;
        } else {
          const unit = hours === 1 ? "hour" : "hours";
          const value = hours % 1 < 0.1 ? hours.toFixed(0) : hours.toFixed(1);
          return `${value} ${unit}`;
        }
      },
    },
  ],
  listeners: {
    selectionChange() {
      const record = this.selectedRecords[0],
        scheduler = window.taskManagementSchedulerRef.current.instance,
        { calendarHighlight } = scheduler.features;

      if (!record) {
        calendarHighlight.highlightResourceCalendars([]);
        return;
      }

      const availableResources = scheduler.resourceStore.query((resource) => {
        const activityResourceIds = Object.keys(
          record.ganttResourceAllocation
        ).map((res) => +res);
        const resourceTeams =
          resource?.data.parentIds.split(",").map((id) => +id) || [];
        return !!activityResourceIds.filter((id) => resourceTeams.includes(id))
          .length;
      });
      calendarHighlight.highlightResourceCalendars(availableResources);
    },
    collapse() {
      const scheduler = window.taskManagementSchedulerRef.current.instance,
        { calendarHighlight } = scheduler.features;

      calendarHighlight.highlightResourceCalendars([]);
    },
  },
});

export class UnassignedTasksDrag extends DragHelper {
  static get configurable() {
    return {
      callOnFunctions: true,
      // Don't drag the actual row element, clone it
      cloneTarget: true,
      // We size the cloned element manually
      autoSizeClonedTarget: false,
      // Only allow drops on the schedule area
      dropTargetSelector: ".b-timeline-subgrid",
      // Only allow drag of row elements inside on the unplanned grid
      targetSelector: ".b-grid-row:not(.b-group-row)",
    };
  }

  afterConstruct() {
    // Configure DragHelper with schedule's scrollManager to allow scrolling while dragging
    this.scrollManager = this.schedule.current.instance.scrollManager;
  }

  createProxy(element) {
    const proxy = document.createElement("div"),
      schedule = this.schedule.current.instance,
      task = this.grid.current.gridInstance.getRecordFromElement(element),
      duration =
        task.genericMetrics.workAtCompletion - task.genericMetrics.workAssigned,
      durationMS = duration * MS_IN_HOUR,
      durationInPx = schedule.timeAxisViewModel.getDistanceForDuration(
        durationMS
      );

    let edge = "";

    // Fake an event bar
    proxy.classList.add(
      "b-sch-event-wrap",
      "b-sch-event",
      "b-unassigned-class",
      `b-sch-${schedule.mode}`,
      "d-flex"
    );
    proxy.style.height = `${
      schedule.rowHeight - 2 * schedule.resourceMargin
    }px`;
    proxy.style.borderRadius = `${
      getSettings("task-management-scheduler-settings").taskBorderRadius || 0
    }px`;

    if (durationInPx > window.outerWidth / 3) {
      proxy.style.width = `${window.outerWidth / 3}px`;

      edge = `
        <div class="custom-event-edge d-flex h-100">
          <div style="margin-right: 12px; width: 3px; background: white;"></div>
          <div style="margin-right: 6px; width: 3px; background: white;"></div>
          <div style="margin-right: 4px; width: 3px; background: white;"></div>
          <div style="margin-right: 2px; width: 3px; background: white;"></div>
        </div>
      `;
    } else {
      proxy.style.width = `${durationInPx}px`;
    }

    proxy.innerHTML = `
      <div class="b-sch-event b-has-content b-sch-event-withicon">
        <div class="b-sch-event-content">
          <b>${task.name}</b>
        </div>
      </div>
      ${edge}
    `;

    return proxy;
  }

  onDragStart({ context }) {
    const me = this,
      schedule = me.schedule.current.instance,
      grid = me.grid.current.gridInstance,
      { eventTooltip, eventDrag } = schedule.features,
      parentContainer = document.getElementById("tasksManagementContainer"),
      { top, bottom, left, right } = parentContainer.getBoundingClientRect(),
      eventRect = context.element.getBoundingClientRect();

    // set limitation on dragging area
    this.minX = left;
    this.maxX = right - eventRect.width;
    this.minY = top;
    this.maxY = bottom - eventRect.height;

    // save a reference to the task so we can access it later
    context.task = grid.getRecordFromElement(context.grabbed);

    // Prevent tooltips from showing while dragging
    eventTooltip.disabled = true;

    schedule.enableScrollingCloseToEdges(schedule.timeAxisSubGrid);

    if (eventDrag.showTooltip && !me.tip) {
      me.tip = new Tooltip({
        align: "b-t",
        forElement: context.element,
        cls: "b-popup b-sch-event-tooltip",
      });
    }
  }

  onDrag({ event, context }) {
    const me = this,
      schedule = me.schedule.current.instance,
      { task } = context,
      coordinate = DomHelper[
        `getTranslate${schedule.isHorizontal ? "X" : "Y"}`
      ](context.element),
      startDate = schedule.getDateFromCoordinate(
        coordinate,
        "round",
        false,
        true
      ),
      work =
        task.genericMetrics.workAtCompletion - task.genericMetrics.workAssigned,
      endDate = startDate && DateHelper.add(startDate, work, "h"),
      // Coordinates required when used in vertical mode, since it does not use actual columns
      resource =
        context.target &&
        schedule.resolveResourceRecord(context.target, [
          event.offsetX,
          event.offsetY,
        ]),
      // check if resource`s team is involved in this activity
      activityResourceIds = Object.keys(task.ganttResourceAllocation).map(
        (res) => +res
      ),
      resourceTeams =
        resource?.data.parentIds.split(",").map((id) => +id) || [],
      resourceValidation = !!activityResourceIds.filter((id) =>
        resourceTeams.includes(id)
      ).length;

    if (task.execDateTime) {
      schedule.timeRangeStore.add(
        schedulerExecDateTimeRanges(formatDate(task.execDateTime))
      );
    }

    // Don't allow drops anywhere, only allow drops if the drop is on the timeaxis and on top of a Resource
    context.valid =
      Boolean(startDate && resource) &&
      resourceValidation &&
      (schedule.allowOverlap ||
        schedule.isDateRangeAvailable(startDate, endDate, null, resource));

    // Save reference to resource so we can use it in onTaskDrop
    context.resource = resource;
    context.startDate = startDate;

    if (me.tip && context.valid) {
      const dateFormat = schedule.displayDateFormat,
        formattedStartDate = DateHelper.format(startDate, dateFormat),
        formattedEndDate = DateHelper.format(endDate, dateFormat);

      me.tip.html = `
        <div><b>${task.name}</b></div>
        <div><i>${task.projectName}</i></div>
        <div class="b-sch-tooltip-startdate">Start: ${formattedStartDate}</div>
      `;
      // <div class="b-sch-tooltip-enddate">Ends: ${formattedEndDate}</div>
      me.tip.showBy(context.element);
    } else {
      me.tip?.hide();
    }
  }

  // Drop callback after a mouse up, take action and transfer the unplanned task to the real EventStore (if it's valid)
  async onDrop({ context, event }) {
    const me = this,
      schedule = me.schedule.current.instance,
      grid = me.grid.current.gridInstance,
      { task, target, resource, valid, element, startDate } = context,
      work =
        task.genericMetrics.workAtCompletion - task.genericMetrics.workAssigned;

    me.tip?.hide();

    schedule.disableScrollingCloseToEdges(me.schedule.timeAxisSubGrid);

    // If drop was done in a valid location, set the startDate and transfer the task to the Scheduler event store
    if (valid && target && startDate) {
      if (task.execDateTime && !areDatesSame(startDate, task.execDateTime)) {
        const result = await openConfirmationPopup(
          startDate,
          task.execDateTime
        );
        if (!result) {
          schedule.timeRangeStore.remove(EXEC_DATETIME_TIME_RANGE_ID);
          return;
        }
      }

      const hoursPerDay = dailyResourceWorkHoursBasedOnShifts(resource.shifts),
        durationInHours =
          task.genericMetrics.workAtCompletion -
          task.genericMetrics.workAssigned,
        durationInDays = durationInHours / hoursPerDay;

      grid.store.remove(task);

      const tempData = {
        id: uuidv4(),
        name: task.name,
        projectName: task.projectName,
        startDate,
        duration: durationInDays,
        resourceId: resource.id,
      };

      const tempTask = schedule.eventStore.add(tempData)[0];
      await schedule.eventStore.project.commitAsync();

      const newTask = {
        name: task.name,
        resourceId: resource.id,
        startDate: removeTimezoneOffset(tempTask.data.startDate),
        finishDate: removeTimezoneOffset(tempTask.data.endDate),
        notes: task.notes,
        predecessors: task.predecessors,
        saId: task.id,
        duration: durationInDays,
        work: durationInHours,
        customColumns: task.customColumns,
        parentSAName: task.saParentName || null,
      };

      dispatchTaskDropEvent(newTask);
    }

    if (resource) {
      resource.cls = "";
    }

    schedule.features.eventTooltip.disabled = false;
    if (task.execDateTime) {
      schedule.timeRangeStore.remove(EXEC_DATETIME_TIME_RANGE_ID);
    }
  }

  set schedule(schedule) {
    this._schedule = schedule;

    // Configure DragHelper with schedule's scrollManager to allow scrolling while dragging
    this.scrollManager = schedule.scrollManager;
  }

  get schedule() {
    return this._schedule;
  }

  onDragAbort() {
    this.tip?.hide();
  }
}

function dispatchTaskDropEvent(newTask) {
  const event = new Event("TaskHasDroppedFromTheGrid");
  event.data = newTask;
  window.dispatchEvent(event);
}

export const openConfirmationPopup = (startDate, execDateTime) => {
 const message = t("myTasks.dateOnAssignDontMatch", {
    assignDate: formatDate(startDate),
    execDate: formatDate(execDateTime)
  });
  return new Promise((resolve) => {
    const popup = new Popup({
      width: "400px",
      positioned: true,
      floating: true,
      centered: true,
      modal: { closeOnMaskTap: true },
      html: message,
      bbar: {
        items: {
          okButton: {
            text: "Confirm",
            cls: "grid-popup-button primary",
            onAction: () => {
              popup.close();
              resolve(true);
            },
          },
          cancelButton: {
            text: "Cancel",
            cls: "grid-popup-button gray",
            onAction: () => {
              popup.close();
              resolve(false);
            },
          },
        },
      },
    });

    popup.show();
  });
};